import { useReducedMotion } from '@mantine/hooks';
import { Line } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import { MathUtils, type Mesh, type MeshBasicMaterial } from 'three';

import {
    getWorldUnitsPerPixel,
    INTERACTION_RENDER_ORDER,
    SNAP_MARKER_RADIUS_PX,
    SNAP_CAPTURE_RADIUS_PX,
    SNAP_PREVIEW_DISTANCE_FACTOR
} from 'app/modules/wallforming/components/scenes/objects/utils/screenSpaceUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import type { Line2 } from 'three-stdlib';

const FADE_SPEED = 20;
const FADE_EPSILON = 0.01;
const noRaycast = () => {};

export const SnapPointPreview = ({ index }: { index?: number }) => {
    const visualRef = useRef<Mesh | null>(null);
    const haloMaterialRef = useRef<MeshBasicMaterial | null>(null);
    const materialRef = useRef<MeshBasicMaterial | null>(null);
    const dotRef = useRef<Mesh | null>(null);
    const auraMaterialRef = useRef<MeshBasicMaterial | null>(null);
    const guideRef = useRef<Line2 | null>(null);
    const reducedMotion = useReducedMotion();

    useFrame(({ camera, size, invalidate }, delta) => {
        const state = usePlacementStore.getState();
        const target = state.snapPointMarkerRef.current;
        const visual = visualRef.current;
        const material = materialRef.current;
        if (!target || !visual || !material) {
            return;
        }

        const additional = index === undefined ? undefined : state.additionalSnapPointFeedback[index];
        const captured = index === undefined ? target.visible : !!additional && !additional.source;
        const preview =
            index === undefined
                ? state.snapPointPreviewRef.current
                : additional?.source
                  ? [additional.target, additional.source]
                  : null;
        const destination = index === undefined ? (captured ? target.position : preview?.[0]) : additional?.target;
        if (destination) {
            visual.position.copy(destination);
        }
        const unitsPerPixel = getWorldUnitsPerPixel(camera, visual.position, size.height);
        const proximity = preview
            ? MathUtils.clamp(
                  1 -
                      preview[0].distanceTo(preview[1]) /
                          (unitsPerPixel * SNAP_CAPTURE_RADIUS_PX * SNAP_PREVIEW_DISTANCE_FACTOR),
                  0,
                  1
              )
            : 0;
        const opacity = captured ? 1 : preview ? 0.25 + proximity * 0.45 : 0;
        material.opacity = reducedMotion ? opacity : MathUtils.damp(material.opacity, opacity, FADE_SPEED, delta);
        if (Math.abs(material.opacity - opacity) < FADE_EPSILON) {
            material.opacity = opacity;
        } else {
            invalidate();
        }
        if (haloMaterialRef.current) {
            haloMaterialRef.current.opacity = material.opacity;
        }
        if (auraMaterialRef.current) {
            auraMaterialRef.current.opacity = material.opacity * 0.15;
        }
        if (dotRef.current) {
            dotRef.current.visible = captured;
        }
        const guide = guideRef.current;
        if (guide) {
            if (preview && !captured) {
                guide.geometry.setPositions([...preview[1].toArray(), ...preview[0].toArray()]);
                guide.computeLineDistances();
            }
            const guideOpacity = preview && !captured ? opacity : 0;
            guide.material.opacity = reducedMotion
                ? guideOpacity
                : MathUtils.damp(guide.material.opacity, guideOpacity, FADE_SPEED, delta);
            if (Math.abs(guide.material.opacity - guideOpacity) < FADE_EPSILON) {
                guide.material.opacity = guideOpacity;
            } else {
                invalidate();
            }
            guide.visible = guide.material.opacity > 0;
        }
        visual.visible = material.opacity > 0;
        if (!visual.visible) {
            return;
        }
        camera.getWorldQuaternion(visual.quaternion);
        visual.scale.setScalar(SNAP_MARKER_RADIUS_PX * unitsPerPixel * (1 + (1 - material.opacity) * 0.3));
    });

    return (
        <>
            <Line
                ref={guideRef}
                color={PRIMARY_COLOR_ACCENT_HEX}
                depthTest={false}
                depthWrite={false}
                lineWidth={3}
                name={index === undefined ? 'snap-point-preview-guide' : `snap-point-preview-guide-${index}`}
                opacity={0}
                points={[
                    [0, 0, 0],
                    [0, 0, 0]
                ]}
                raycast={noRaycast}
                renderOrder={INTERACTION_RENDER_ORDER}
                toneMapped={false}
                transparent
            />
            <mesh
                ref={visualRef}
                name={index === undefined ? 'snap-point-feedback' : `snap-point-feedback-${index}`}
                raycast={noRaycast}
                renderOrder={INTERACTION_RENDER_ORDER}
                visible={false}
            >
                <ringGeometry args={[0.5, 1, 32]} />
                <meshBasicMaterial
                    ref={materialRef}
                    color={PRIMARY_COLOR_ACCENT_HEX}
                    depthTest={false}
                    depthWrite={false}
                    opacity={0}
                    toneMapped={false}
                    transparent
                />
                <mesh raycast={noRaycast} renderOrder={INTERACTION_RENDER_ORDER}>
                    <ringGeometry args={[1, 1.25, 32]} />
                    <meshBasicMaterial
                        ref={haloMaterialRef}
                        color="white"
                        depthTest={false}
                        depthWrite={false}
                        toneMapped={false}
                        transparent
                    />
                </mesh>
                <mesh raycast={noRaycast} renderOrder={INTERACTION_RENDER_ORDER - 1}>
                    <circleGeometry args={[1.8, 32]} />
                    <meshBasicMaterial
                        ref={auraMaterialRef}
                        color={PRIMARY_COLOR_ACCENT_HEX}
                        depthTest={false}
                        depthWrite={false}
                        opacity={0}
                        toneMapped={false}
                        transparent
                    />
                </mesh>
                <mesh
                    ref={dotRef}
                    name={index === undefined ? 'snap-point-captured' : `snap-point-captured-${index}`}
                    raycast={noRaycast}
                    renderOrder={INTERACTION_RENDER_ORDER}
                >
                    <circleGeometry args={[0.25, 16]} />
                    <meshBasicMaterial
                        color={PRIMARY_COLOR_ACCENT_HEX}
                        depthTest={false}
                        depthWrite={false}
                        toneMapped={false}
                    />
                </mesh>
            </mesh>
        </>
    );
};
